import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";
import { Card, PageHeader, Btn, Field, inputCls, StatusPill, Empty } from "../../components/dash/Kit";
import { api, errMessage } from "../../lib/api";

const topics = ["Sales", "Account", "Billing", "Content", "Technical Issue", "Other"];

const LABEL = { open: "Waiting for us", answered: "We replied", closed: "Closed" };

export default function Support() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    topic: params.get("topic") || "Content",
    message: params.get("about") ? `About: ${params.get("about")}\n\n` : "",
  });
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [replies, setReplies] = useState({});
  const [replyBusy, setReplyBusy] = useState("");

  const load = () => api.get("/support").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (form.message.trim().length < 10) {
      setError("Please tell us a little more (at least 10 characters).");
      return;
    }
    setError("");
    setBusy(true);
    try {
      await api.post("/support", form);
      toast.success("Message sent. We'll come back to you by email too.");
      setForm({ topic: "Content", message: "" });
      load();
    } catch (err) {
      toast.error(errMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const sendReply = async (ticketId) => {
    const message = (replies[ticketId] || "").trim();
    if (!message) {
      toast.error("Write a message first.");
      return;
    }
    setReplyBusy(ticketId);
    try {
      await api.post(`/support/${ticketId}/reply`, { message });
      setReplies((r) => ({ ...r, [ticketId]: "" }));
      toast.success("Reply sent.");
      load();
    } catch (err) {
      toast.error(errMessage(err));
    } finally {
      setReplyBusy("");
    }
  };

  return (
    <div className="space-y-6" data-testid="support-page">
      <PageHeader
        title="Support"
        body="Ask us anything about your account, billing or content. We reply here and by email — Grow and Pro customers are handled first."
      />

      <form onSubmit={submit} noValidate data-testid="support-form">
        <Card>
          <Field label="Topic">
            <select
              data-testid="support-topic-select"
              className={inputCls}
              value={form.topic}
              onChange={(e) => setForm((f) => ({ ...f, topic: e.target.value }))}
            >
              {topics.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </Field>
          <div className="mt-5">
            <Field label="Message" error={error} htmlFor="support-message">
              <textarea
                id="support-message"
                rows={5}
                data-testid="support-message-input"
                className={inputCls}
                value={form.message}
                onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
              />
            </Field>
          </div>
          <Btn type="submit" testId="support-submit-button" className="mt-6" disabled={busy}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {busy ? "Sending…" : "Send Message"}
          </Btn>
        </Card>
      </form>

      <div>
        <p className="mb-3 font-display text-lg font-semibold text-slate-900">Your conversations</p>
        {rows.length === 0 ? (
          <Empty testId="support-empty" title="No messages yet" body="Your support conversations appear here." />
        ) : (
          <div className="space-y-3" data-testid="support-list">
            {rows.map((t) => {
              const thread = t.messages?.length
                ? t.messages
                : [{ id: "legacy", author: "customer", body: t.message, created_at: t.created_at }];
              return (
                <Card key={t.id} testId={`support-row-${t.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{t.topic}</p>
                      <p className="mt-1 text-xs text-slate-400">
                        Started {new Date(t.created_at).toLocaleString()}
                      </p>
                    </div>
                    <StatusPill status={LABEL[t.status] || t.status} testId={`support-status-${t.id}`} />
                  </div>

                  <ul className="mt-4 space-y-3" data-testid={`support-thread-${t.id}`}>
                    {thread.map((m, i) => (
                      <li
                        key={m.id || i}
                        className={`max-w-2xl rounded-2xl p-3 text-sm ${
                          m.author === "postmint"
                            ? "bg-[#FFF3EF] text-slate-800"
                            : "ml-auto bg-slate-100 text-slate-800"
                        }`}
                      >
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                          {m.author === "postmint" ? "PostMint" : "You"}
                        </p>
                        <p className="mt-1 whitespace-pre-line">{m.body}</p>
                        <p className="mt-1.5 text-xs text-slate-400">
                          {m.created_at ? new Date(m.created_at).toLocaleString() : ""}
                        </p>
                      </li>
                    ))}
                  </ul>

                  {t.status !== "closed" ? (
                    <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                      <input
                        value={replies[t.id] || ""}
                        onChange={(e) => setReplies((r) => ({ ...r, [t.id]: e.target.value }))}
                        placeholder="Write a reply…"
                        data-testid={`support-reply-input-${t.id}`}
                        className={inputCls}
                      />
                      <Btn
                        onClick={() => sendReply(t.id)}
                        disabled={replyBusy === t.id}
                        testId={`support-reply-button-${t.id}`}
                        className="shrink-0"
                      >
                        {replyBusy === t.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                        Reply
                      </Btn>
                    </div>
                  ) : (
                    <p className="mt-4 text-sm text-slate-500">
                      This conversation is closed. Send a new message above if you need anything else.
                    </p>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
